import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Wrench, Truck } from 'lucide-react';
import { productUrl } from '../../content/products';
import { toCents, fmt, fittedFromCents } from '../../lib/shopPricing';

// Prose renderer for trusted HTML from content files (same rule as BlogPost).
export const proseClasses =
  '[&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-gray-900 [&_h2]:mt-8 [&_h2]:mb-3 ' +
  '[&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-gray-900 [&_h3]:mt-6 [&_h3]:mb-2 ' +
  '[&_p]:text-gray-700 [&_p]:leading-relaxed [&_p]:mb-4 ' +
  '[&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-5 [&_ul]:space-y-1 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-5 [&_ol]:space-y-1 [&_li]:text-gray-700 ' +
  '[&_a]:text-red-600 [&_a]:font-semibold hover:[&_a]:underline [&_strong]:text-gray-900';

export const Html = ({ html, className = '' }) => (
  <div className={`${proseClasses} ${className}`} dangerouslySetInnerHTML={{ __html: html }} />
);

export const Breadcrumbs = ({ crumbs }) => (
  <nav aria-label="Breadcrumb" className="text-sm text-gray-500 mb-6 flex flex-wrap items-center gap-1">
    {crumbs.map((c, i) => (
      <React.Fragment key={c.path || c.name}>
        {i > 0 && <ChevronRight size={14} className="text-gray-400" />}
        {i === crumbs.length - 1 || !c.path ? (
          <span className="text-gray-700">{c.name}</span>
        ) : (
          <Link to={c.path} className="hover:text-blue-600">
            {c.name}
          </Link>
        )}
      </React.Fragment>
    ))}
  </nav>
);

// Light and warm, not a black band: people on these pages are thinking about their own
// front door (Hugh, 17 Sep 2026). `image` puts one of our own door photos beside the title
// on wide screens.
export const PageHero = ({ title, sub, children, image }) => (
  <section className="bg-gradient-to-br from-sky-50 via-white to-amber-50 border-b border-gray-100 py-12 sm:py-16 text-gray-900">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-10">
      <div className="max-w-3xl flex-1 min-w-0">
        <h1 className="text-4xl sm:text-5xl font-bold mb-4">{title}</h1>
        {sub && <p className="text-xl text-gray-700">{sub}</p>}
        {children}
      </div>
      {image && (
        <img src={image.sm || image.src} alt={image.alt || ''} style={image.pos ? { objectPosition: image.pos } : undefined} className="hidden lg:block w-72 xl:w-80 aspect-[4/3] object-cover rounded-2xl shadow-lg flex-shrink-0" loading="eager" />
      )}
    </div>
  </section>
);

export const STATUS_BADGE = {
  'install-only': { label: 'Supply & install only', cls: 'bg-red-50 text-red-800 border-red-200' },
  quote: { label: 'Quoted per door', cls: 'bg-blue-50 text-blue-800 border-blue-200' },
  'coming-soon': { label: 'Coming soon', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
};

// Product image with the small rendition for cards/thumbnails and the full one for
// wide layouts. Imported images carry `sm` + width/height (see products.js `img`);
// placeholders are SVGs with neither. `eager` = above the fold (product hero).
export const ProductImg = ({ image, sizes = '(min-width: 1024px) 320px, 50vw', className = '', eager = false }) => (
  <img
    src={image.sm || image.src}
    srcSet={image.sm ? `${image.sm} 480w, ${image.src} ${image.width || 1200}w` : undefined}
    sizes={image.sm ? sizes : undefined}
    width={image.width}
    height={image.height}
    alt={image.alt}
    className={className}
    loading={eager ? 'eager' : 'lazy'}
    decoding="async"
  />
);

// Two or three words that say what door the lock is for, from the fit data — the card's
// only line under the name (the blurb went; Hugh, 17 Sep: less copy). `sizes` false leaves
// the millimetres off: a Find-my-lock visitor never hears about the backset (Hugh, 22 Sep 2026).
export const fitTag = (product, sizes = true) => {
  const fit = product.fit || {};
  const b = fit.backsetsMm || [];
  const mm = sizes && b.length ? ` · ${b.join(' / ')} mm` : '';
  if (fit.sliding) return 'Sliding doors' + mm;
  if (b.length > 1 && b[b.length - 1] <= 45) return 'Narrow aluminium stiles';
  if (b.length > 1 && b[0] <= 40) return 'Narrow stile to timber';
  if (fit.faceMounted) return 'Fits on the door face';
  if (/retrofit|round-bore|bore|deadbolt/i.test(fit.lockType || '') && b.length) return 'Round-bore swap' + mm;
  if (b.length && b[0] >= 50) return 'Timber & wide aluminium doors';
  if (b.length) return sizes ? `${b.join(' / ')} mm backset` : 'Hinged doors';
  return '';
};

// `query` (e.g. "?slides=swings&material=aluminium&door=mortice") carries the chooser's door
// answers to the page. `chips` = [{ text, tone: 'reason' | 'warn' }] from the chooser (two-word
// reasons and warnings); `pickBadge` marks "Our pick"; `onClick` is for analytics only;
// `sizes` false hides the millimetres (chooser results and pages).
export const ProductCard = ({ product, query = '', chips = [], pickBadge = null, onClick = null, sizes = true }) => {
  const fitted = fittedFromCents(product);
  const status = product.saleStatus || 'sale';
  const badge = STATUS_BADGE[status];
  const tag = fitTag(product, sizes);
  return (
    <Link
      to={productUrl(product) + query}
      onClick={onClick || undefined}
      className={`group block bg-white border rounded-xl overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all ${pickBadge ? 'border-red-300 ring-2 ring-red-100' : 'border-gray-200 hover:border-gray-300'}`}
      data-testid={`product-card-${product.slug}`}
    >
      {/* The lock is the card: a square, edge-to-edge picture on a soft backdrop, a touch
          bigger on hover (Hugh, 17 Sep 2026: larger images, make the locks pop). */}
      <div className="aspect-[4/5] bg-gradient-to-b from-gray-100 via-gray-50 to-white flex items-center justify-center p-2 overflow-hidden">
        <ProductImg image={product.images[0]} className="max-h-full max-w-full object-contain drop-shadow-lg scale-105 transition-transform duration-300 group-hover:scale-110" />
      </div>
      <div className="p-4 pt-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{product.brand}</p>
        <span className="flex items-center gap-1">
          {pickBadge && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-600 text-white" data-testid="pick-badge">{pickBadge}</span>}
          {badge && <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${badge.cls}`}>{badge.label}</span>}
        </span>
      </div>
      <h3 className="font-bold text-gray-900 group-hover:text-blue-700 leading-snug">{product.name}</h3>
      {tag && <p className="text-xs text-gray-500 mt-1">{tag}</p>}
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2" data-testid="card-chips">
          {chips.map((c) => (
            <span key={c.text} className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${c.tone === 'warn' ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'bg-gray-100 text-gray-700'}`}>{c.text}</span>
          ))}
        </div>
      )}
      <div className="mt-3 space-y-0.5">
        {status === 'quote' && <p className="text-sm text-gray-700">From <span className="font-bold text-gray-900">{fmt(toCents(product.priceIncGst))}</span> · quoted per door</p>}
        {status === 'coming-soon' && <p className="text-sm text-gray-700">Coming soon</p>}
        {status !== 'quote' && status !== 'coming-soon' && fitted && (
          <p className="text-sm font-semibold text-red-700 flex items-center gap-1.5">
            <Wrench size={14} /> <span>{fmt(fitted)} fitted</span>
          </p>
        )}
        {status === 'sale' && (
          <p className="text-gray-900 flex items-center gap-1.5">
            <Truck size={14} className="text-gray-500" />
            <span className="font-bold">{fmt(toCents(product.priceIncGst))}</span>
            <span className="text-sm text-gray-500">supply only</span>
          </p>
        )}
      </div>
      </div>
    </Link>
  );
};

export const Section = ({ title, children, className = '' }) => (
  <section className={`py-12 sm:py-16 ${className}`}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {title && <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">{title}</h2>}
      {children}
    </div>
  </section>
);
